'use client';

import { ReactNode, useRef } from 'react';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { Button } from '@flipster/react/design-system';
import { Video } from '@flipster/react/design-utils/video';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, vstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { MainEvent } from '@flipster/utils/segment/events/main';
import { ProdStaticPath } from '@flipster/utils/url';

import { ContinueWithApple } from '@web/app/[locale]/(web)/_auth/continue-with-apple';
import { ContinueWithGoogle } from '@web/app/[locale]/(web)/_auth/continue-with-google';
import { maxWStylesWithMargin } from '@web/app/[locale]/(web)/_main-page/_styles';
import { useMainPageIntersectionSegment } from '@web/app/[locale]/(web)/_main-page/hooks/useIntersectionSegment';

import { DownloadAppButton } from '../download-app-button';
import EmailTextFieldCTA from './EmailTextFieldCTA';

interface Props {
  mainTitle: ReactNode;
  subTitle?: ReactNode;
  tradePath: string;
}

const HeroLayout = ({ mainTitle, subTitle, tradePath }: Props) => {
  const intersectionRef = useRef<HTMLDivElement>(null);

  const isMounted = useMounted();
  const { isLoggedIn } = useAuth();
  useMainPageIntersectionSegment(intersectionRef, 'Zero Friction Perps.');

  return (
    <>
      <Video
        poster={{
          base: `${ProdStaticPath}/images/main-hero-mb.webp`,
          tablet: `${ProdStaticPath}/images/main-hero-dt.webp`,
        }}
        src={{
          base: `${ProdStaticPath}/videos/main-hero-mb.mp4`,
          tablet: `${ProdStaticPath}/videos/main-hero-dt.mp4`,
        }}
        css={{
          zIndex: -1,
          pos: 'absolute',
          top: 0,
          left: '50%',
          w: '100%',
          maxW: { base: 360, tablet: 1536 },
          maxH: { base: 274, tablet: 640 },
          transform: 'translateX(-50%)',
        }}
      />
      <section
        className={css(
          maxWStylesWithMargin,
          vstack.raw({
            my: { base: 56, tablet: 100 },
            pt: { base: 46, smallDesktop: 140 },
            pb: { smallDesktop: 130 },
          }),
        )}
      >
        <h1
          className={flex({
            flexDirection: { base: 'column', smallDesktop: 'row' },
            align: 'center',
            textStyle: 'Display/XXL',
            textAlign: 'center',
            color: 'content.default.level-5',
            gap: { smallDesktop: 16 },
            mb: { base: 4, smallDesktop: 24 },
          })}
          ref={intersectionRef}
        >
          {mainTitle}
        </h1>
        {subTitle}
        {isMounted && isLoggedIn ? (
          <div className={vstack({ w: '100%', maxW: { tablet: 229 }, gap: { base: 12, tablet: 32 } })}>
            <Button
              hierarchy='primary'
              color='accent'
              size='large'
              as='a'
              href={tradePath}
              label='Trade now'
              onClick={() => segment.track(MainEvent.MainPageTradeNowClick)}
              css={{ w: '100%' }}
            />
            <DownloadAppButton />
          </div>
        ) : (
          <div className={css({ w: '100%', maxW: { tablet: 470 } })}>
            <div className={css({ mb: 16 })}>
              <EmailTextFieldCTA />
            </div>
            <div
              className={flex({
                justify: 'space-between',
                flexDirection: { base: 'column', tablet: 'row' },
                gap: 24,
              })}
            >
              <div
                className={flex({
                  align: { tablet: 'center' },
                  flexDirection: { base: 'column', tablet: 'row' },
                  textStyle: 'Text/M/Strong',
                  color: 'content.default.level-2',
                  gap: 8,
                })}
              >
                <p>Continue with</p>
                <div className={hstack({ gap: 8 })}>
                  <ContinueWithGoogle
                    label={<span className={css({ display: { tablet: 'none' } })}>Google</span>}
                    css={{ w: { base: '100%', tablet: 50 } }}
                  />
                  <ContinueWithApple
                    label={<span className={css({ display: { tablet: 'none' } })}>Apple</span>}
                    css={{ w: { base: '100%', tablet: 50 } }}
                  />
                </div>
              </div>
              <DownloadAppButton />
            </div>
          </div>
        )}
      </section>
    </>
  );
};

const SpotHero = () => {
  return (
    <HeroLayout
      mainTitle={
        <>
          <p>Crypto Made </p>
          <p className={css({ color: 'content.accent.info' })}>Simple.</p>
        </>
      }
      subTitle={
        <p
          className={css({
            textStyle: { base: 'Text/M/Strong', tablet: 'Title/S' },
            color: 'content.default.level-5',
            mb: 32,
          })}
        >
          A secure, trusted way to trade
        </p>
      }
      tradePath='/trade/spot/latest'
    />
  );
};

export default SpotHero;
